import React from 'react';

/** Defined in index.css so the hairline can dilute `currentColor`, which no
 *  Tailwind decoration utility can do. Exported so tests select what ships. */
export const UNDERLINE_CLASS = 'mnemonic-underline';

/**
 * Flatten a label to plain text.
 *
 * Underlining a character splits the text node, and accessible-name computation
 * joins element boundaries with a space — "Save" becomes "S ave" to a screen
 * reader. Callers pass this as an explicit aria-label to keep the spoken name
 * intact while the underline stays visible.
 */
export function extractLabelText(children) {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractLabelText).join('');
  if (React.isValidElement(children)) return extractLabelText(children.props?.children);
  return '';
}

function underlineIn(text, char) {
  const index = text.toLowerCase().indexOf(char.toLowerCase());
  if (index === -1) return null;
  return [
    text.slice(0, index),
    <span key="m" className={UNDERLINE_CLASS}>{text[index]}</span>,
    text.slice(index + 1),
  ];
}

/**
 * Underlines one occurrence of `char` in the first text node of `children`.
 *
 * Most buttons here render an icon plus text rather than a bare string, so this
 * walks the tree instead of assuming children is a string. When the character
 * is absent from the label entirely we append a "(S)" hint rather than silently
 * rendering a binding the user cannot see.
 */
export function MnemonicLabel({ char, children }) {
  if (!char) return children;

  let done = false;

  const walk = (node) => {
    if (done) return node;

    if (typeof node === 'string') {
      const replaced = underlineIn(node, char);
      if (!replaced) return node;
      done = true;
      return replaced;
    }

    if (Array.isArray(node)) return React.Children.map(node, walk);

    if (React.isValidElement(node) && node.props?.children) {
      const nested = walk(node.props.children);
      return nested === node.props.children ? node : React.cloneElement(node, { children: nested });
    }

    return node;
  };

  const result = walk(children);
  if (done) return result;

  return (
    <>
      {children}
      <span>
        {' ('}
        <span className={UNDERLINE_CLASS}>{char.toUpperCase()}</span>
        {')'}
      </span>
    </>
  );
}

export default MnemonicLabel;
